// src/pages/league-dashboard/live-draft/components/IncomingTradeNotification.js
// Toast notification for incoming trade proposals
import React, { useEffect, useState } from 'react';
import { ArrowLeftRight, Eye, X } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';

export const IncomingTradeNotification = React.memo(({
  trade,
  fromTeam,
  onView,
  onDismiss,
  autoHideSeconds = 30
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(autoHideSeconds);

  // Auto-dismiss countdown
  useEffect(() => {
    if (timeRemaining <= 0) {
      setIsVisible(false);
      onDismiss?.();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, onDismiss]);

  // Handle view click
  const handleView = () => {
    onView(trade);
    setIsVisible(false);
  };

  // Handle dismiss click
  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  // Count assets in trade
  const offeringCount = (trade.offering_picks?.length || 0) + (trade.offering_players?.length || 0);
  const requestingCount = (trade.requesting_picks?.length || 0) + (trade.requesting_players?.length || 0);

  return (
    <div className="fixed top-4 right-4 z-[60] animate-slide-in-right">
      <div className={`${dynastyTheme.components.card.base} p-4 max-w-sm shadow-2xl border-l-4 border-yellow-500`}>
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <ArrowLeftRight className="w-5 h-5 text-yellow-500" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className={`font-bold ${dynastyTheme.classes.text.primary}`}>
              Trade Proposal!
            </h4>
            <p className={`text-sm mt-1 ${dynastyTheme.classes.text.white}`}>
              <span className="font-medium">{fromTeam?.team_name || 'A team'}</span>
              {' '}wants to trade with you
            </p>

            {/* Trade summary */}
            <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
              {requestingCount} asset(s) for {offeringCount} asset(s)
            </p>

            {/* Actions */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleView}
                className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black rounded text-sm font-medium flex items-center gap-1 transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                View Trade
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded text-sm text-white transition-colors"
              >
                Dismiss ({timeRemaining}s)
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-yellow-500 transition-all duration-1000 ease-linear"
            style={{ width: `${(timeRemaining / autoHideSeconds) * 100}%` }}
          />
        </div>
      </div>

      {/* Slide-in animation styles */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
});

IncomingTradeNotification.displayName = 'IncomingTradeNotification';

export default IncomingTradeNotification;
